import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  V2FormField,
  Select,
  Icon,
  LoadingState,
  StatePanel,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
  V2Kpi,
  V2MetricStrip,
  V2Table,
  type V2BadgeTone,
  type V2TableColumn,
  V2FormGrid,
  V2FormActions,
} from '@sakhtar/design-system/react';
import { apiPatch, apiPost, ApiError } from '../../lib/apiClient.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate, displayJalaliDateTime } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import {
  PROJECT_STATUS_OPTIONS,
  TASK_PRIORITY_OPTIONS,
  type AssignableMembersResponse,
  type ProjectDetail,
  type ProjectMembersResponse,
  type ProjectTask,
} from '../../lib/types/projects.js';
import { ProjectTeamPanel } from './ProjectTeamPanel.js';
import './project-detail.css';

type ProjectStatus = (typeof PROJECT_STATUS_OPTIONS)[number]['value'];

const STATUS_BY_LABEL = new Map<string, ProjectStatus>(
  PROJECT_STATUS_OPTIONS.map((opt) => [opt.label, opt.value]),
);

const STATUS_TONE: Record<string, V2BadgeTone> = {
  برنامه‌ریزی: 'neutral',
  فعال: 'success',
  متوقف: 'warn',
  تکمیل‌شده: 'info',
  'لغو شده': 'danger',
};

const TASK_STATUS_TONE: Record<string, V2BadgeTone> = {
  'انجام نشده': 'neutral',
  'در حال انجام': 'info',
  انجام‌شده: 'success',
  'لغو شده': 'danger',
};

interface StatusAction {
  readonly to: ProjectStatus;
  readonly label: string;
  readonly variant: 'primary' | 'secondary' | 'danger';
  readonly confirm?: string;
}

/**
 * PROJECT_STATUS_TRANSITIONS mirror server matrix (packages/modules/projects/src/transitions.ts):
 * - planning → active | cancelled
 * - active → on_hold | completed | cancelled
 * - on_hold → active | cancelled
 * - completed / cancelled are terminal.
 */
const STATUS_ACTIONS: Record<ProjectStatus, readonly StatusAction[]> = {
  planning: [
    { to: 'active', label: 'شروع پروژه', variant: 'primary' },
    {
      to: 'cancelled',
      label: 'لغو پروژه',
      variant: 'danger',
      confirm: 'پروژه لغو شود؟ پروژهٔ لغوشده دیگر فعال نمی‌شود.',
    },
  ],
  active: [
    {
      to: 'completed',
      label: 'تکمیل پروژه',
      variant: 'primary',
      confirm: 'پروژه تکمیل شود؟',
    },
    { to: 'on_hold', label: 'توقف موقت', variant: 'secondary' },
    {
      to: 'cancelled',
      label: 'لغو پروژه',
      variant: 'danger',
      confirm: 'پروژه لغو شود؟ پروژهٔ لغوشده دیگر فعال نمی‌شود.',
    },
  ],
  on_hold: [
    { to: 'active', label: 'ادامهٔ پروژه', variant: 'primary' },
    {
      to: 'cancelled',
      label: 'لغو پروژه',
      variant: 'danger',
      confirm: 'پروژه لغو شود؟ پروژهٔ لغوشده دیگر فعال نمی‌شود.',
    },
  ],
  completed: [],
  cancelled: [],
};

interface TaskRow extends ProjectTask {
  readonly id: string;
}

/**
 * Project detail view — GAP-PP-04 anti-orphan detail route /projects/list/:ref.
 * Real endpoint: GET/PATCH t/:slug/api/v1/projects/:ref (apps/api/src/projects/hub.controller.ts)
 * Team endpoint: GET t/:slug/api/v1/projects/:ref/members
 * Comments endpoint: POST t/:slug/api/v1/projects/comments
 * Tasks endpoint: POST t/:slug/api/v1/projects/tasks
 */
export function ProjectDetailPage() {
  const { ref } = useParams<{ ref: string }>();
  return <ProjectDetailView key={ref} />;
}

function ProjectDetailView() {
  const { slug, ref } = useParams<{ slug: string; ref: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);

  // Status transitions
  const [pendingStatus, setPendingStatus] = useState<ProjectStatus | null>(null);
  const [confirmingAction, setConfirmingAction] = useState<StatusAction | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusNotice, setStatusNotice] = useState<string | null>(null);

  // Team panel modal
  const [showTeamPanel, setShowTeamPanel] = useState(false);

  // New task form inside project
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskPriority, setTaskPriority] = useState('medium');
  const [taskAssigneeRef, setTaskAssigneeRef] = useState('');
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [taskError, setTaskError] = useState<string | null>(null);

  // New comment
  const [commentBody, setCommentBody] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  const project = useApiGet<ProjectDetail>(
    slug && ref ? `${base}/api/v1/projects/${encodeURIComponent(ref)}` : null,
    [slug, ref, refreshKey],
  );

  const team = useApiGet<ProjectMembersResponse>(
    slug && ref ? `${base}/api/v1/projects/${encodeURIComponent(ref)}/members` : null,
    [slug, ref, refreshKey],
  );

  const assignable = useApiGet<AssignableMembersResponse>(
    slug && ref
      ? `${base}/api/v1/projects/assignable-members?projectRef=${encodeURIComponent(ref)}`
      : null,
    [slug, ref, refreshKey],
  );

  interface ProjectProcurementsResponse {
    readonly projectRef: string;
    readonly items: readonly {
      readonly requestCode: string | null;
      readonly requestStatus: string | null;
      readonly requestTotalRial: string | null;
      readonly orderCode: string | null;
      readonly orderStatus: string | null;
      readonly orderTotalRial: string | null;
      readonly orderDate: string | null;
    }[];
  }

  const procurements = useApiGet<ProjectProcurementsResponse>(
    slug && ref ? `${base}/api/v1/projects/${encodeURIComponent(ref)}/procurements` : null,
    [slug, ref, refreshKey],
  );

  if (!slug || !ref) return null;

  const backLink = (
    <Link
      to={`${base}/projects/list`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        color: 'var(--prj-link, var(--sk-brand-600))',
        textDecoration: 'none',
        fontWeight: 500,
      }}
    >
      → بازگشت به پروژه‌ها
    </Link>
  );

  if (project.status === 'loading') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {backLink}
        <LoadingState label="در حال دریافت اطلاعات پروژه…" />
      </div>
    );
  }

  if (project.status === 'error') {
    const status = project.error.status;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {backLink}
        {status === 403 ? (
          <StatePanel
            variant="permission_denied"
            title="دسترسی ندارید"
            description="اجازهٔ مشاهدهٔ این پروژه را ندارید."
          />
        ) : status === 404 ? (
          <StatePanel
            variant="not_found"
            title="پروژه یافت نشد"
            description="پروژه‌ای با این شناسه وجود ندارد یا حذف شده است."
          />
        ) : (
          <StatePanel
            variant="network_error"
            title="اطلاعات پروژه قابل نمایش نیست"
            onRetry={() => setRefreshKey((k) => k + 1)}
          />
        )}
      </div>
    );
  }

  const p = project.data;
  const statusKey = STATUS_BY_LABEL.get(p.statusLabel);
  const actions = statusKey ? STATUS_ACTIONS[statusKey] : [];
  const canManageTeam = team.status === 'ready' && team.data.canManage;
  const teamMembers = team.status === 'ready' ? team.data.members : [];
  const activeAssignableMembers = assignable.status === 'ready' ? assignable.data.members : [];

  const taskRows: TaskRow[] = p.tasks.map((t) => ({ ...t, id: t.ref }));

  // Task KPI calculations
  const totalTasks = p.taskSummary?.total ?? p.tasks.length;
  const inProgressTasks = p.tasks.filter((t) => t.statusLabel === 'در حال انجام').length;
  const todoTasks = p.tasks.filter((t) => t.statusLabel === 'انجام نشده').length;
  const doneTasks = p.tasks.filter((t) => t.statusLabel === 'انجام‌شده').length;

  async function onTransitionStatus(action: StatusAction) {
    setConfirmingAction(null);
    setStatusError(null);
    setStatusNotice(null);
    setPendingStatus(action.to);
    try {
      await apiPatch(`${base}/api/v1/projects/${encodeURIComponent(ref!)}`, {
        status: action.to,
      });
      setStatusNotice(`وضعیت پروژه با موفقیت به «${labelOf(action.to)}» تغییر یافت.`);
      setRefreshKey((k) => k + 1);
    } catch (caught) {
      if (caught instanceof ApiError && (caught.status === 403 || caught.status === 404)) {
        setStatusError('اجازهٔ تغییر وضعیت این پروژه را ندارید.');
      } else if (caught instanceof ApiError && caught.status === 422) {
        setStatusError('این تغییر وضعیت مجاز نیست؛ صفحه را دوباره بارگذاری کنید.');
      } else {
        setStatusError('تغییر وضعیت ناموفق بود.');
      }
    } finally {
      setPendingStatus(null);
    }
  }

  async function onCreateTask(e: FormEvent) {
    e.preventDefault();
    setTaskError(null);
    if (!taskTitle.trim()) {
      setTaskError('عنوان کار الزامی است.');
      return;
    }
    setTaskSubmitting(true);
    try {
      const payload: {
        projectRef: string;
        title: string;
        priorityLabel?: string;
        dueDate?: string;
        assigneeMemberRef?: string;
      } = {
        projectRef: ref!,
        title: taskTitle.trim(),
      };
      if (taskPriority) payload.priorityLabel = taskPriority;
      if (taskDueDate) payload.dueDate = taskDueDate;
      if (taskAssigneeRef) payload.assigneeMemberRef = taskAssigneeRef;

      await apiPost(`${base}/api/v1/projects/tasks`, payload);
      setTaskTitle('');
      setTaskDueDate('');
      setTaskPriority('medium');
      setTaskAssigneeRef('');
      setShowNewTaskForm(false);
      setRefreshKey((k) => k + 1);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        setTaskError('شما اجازهٔ ایجاد کار در این پروژه را ندارید.');
      } else if (caught instanceof ApiError && caught.status === 422) {
        setTaskError('اطلاعات کار نامعتبر است.');
      } else {
        setTaskError('ثبت کار ناموفق بود.');
      }
    } finally {
      setTaskSubmitting(false);
    }
  }

  async function onPostComment(e: FormEvent) {
    e.preventDefault();
    if (!commentBody.trim()) return;
    setCommentSubmitting(true);
    setCommentError(null);
    try {
      await apiPost(
        `${base}/api/v1/projects/comments`,
        {
          targetType: 'project',
          targetRef: ref,
          body: commentBody.trim(),
        },
        { headers: { 'idempotency-key': crypto.randomUUID() } },
      );
      setCommentBody('');
      setRefreshKey((k) => k + 1);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 403) {
        setCommentError('شما اجازهٔ ثبت نظر در این پروژه را ندارید.');
      } else {
        setCommentError('ثبت نظر ناموفق بود.');
      }
    } finally {
      setCommentSubmitting(false);
    }
  }

  const taskColumns: readonly V2TableColumn<TaskRow>[] = [
    {
      key: 'title',
      header: 'کار',
      render: (task) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Link
            to={`${base}/projects/tasks/${encodeURIComponent(task.ref)}`}
            style={{
              fontWeight: 600,
              color: 'var(--prj-link, var(--sk-brand-600))',
              textDecoration: 'none',
            }}
          >
            {task.title}
          </Link>
          <span
            className="ltr"
            style={{
              fontSize: 12,
              color: 'var(--sk-fg-muted)',
              fontFamily: 'var(--sk-font-mono)',
            }}
          >
            {task.ref}
          </span>
        </div>
      ),
    },
    {
      key: 'assignee',
      header: 'مسئول',
      render: (task) => (
        <span
          style={{ fontSize: 13, color: task.assignee ? 'var(--sk-fg)' : 'var(--sk-fg-muted)' }}
        >
          {task.assignee?.displayLabel ?? 'بدون مسئول'}
        </span>
      ),
    },
    {
      key: 'dueDate',
      header: 'سررسید',
      render: (task) => (
        <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
          {displayJalaliDate(task.dueDate)}
        </span>
      ),
    },
    {
      key: 'priority',
      header: 'اولویت',
      render: (task) => <V2Badge tone="neutral">{task.priorityLabel}</V2Badge>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (task) => (
        <V2Badge tone={TASK_STATUS_TONE[task.statusLabel] ?? 'neutral'}>{task.statusLabel}</V2Badge>
      ),
    },
    {
      key: 'actions',
      header: 'اقدام',
      render: (task) => (
        <Link
          to={`${base}/projects/tasks/${encodeURIComponent(task.ref)}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 10px',
            fontSize: 12,
            borderRadius: 'var(--sk-r-sm)',
            border: '1px solid var(--sk-border)',
            background: 'var(--sk-bg-canvas)',
            color: 'var(--sk-fg)',
            textDecoration: 'none',
          }}
        >
          مشاهده
        </Link>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {backLink}

      {/* Project Overview Card */}
      <V2Card>
        <V2CardHeader
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span>{p.name}</span>
              <V2Badge tone={STATUS_TONE[p.statusLabel] ?? 'neutral'}>{p.statusLabel}</V2Badge>
            </div>
          }
          subtitle={
            <span
              className="ltr"
              style={{
                fontFamily: 'var(--sk-font-mono)',
                fontSize: 13,
                color: 'var(--sk-fg-muted)',
              }}
            >
              {p.ref}
            </span>
          }
          actions={
            canManageTeam ? (
              <V2Button
                variant="secondary"
                size="sm"
                icon="users"
                onClick={() => setShowTeamPanel(true)}
              >
                مدیریت تیم
              </V2Button>
            ) : null
          }
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
            padding: '12px 20px 20px',
            borderTop: '1px solid var(--sk-divider)',
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 4 }}>سررسید</div>
            <div>
              {p.dueDate ? (
                p.isOverdue ? (
                  <span
                    style={{
                      color: 'var(--prj-danger-text, var(--sk-danger))',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontWeight: 500,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <Icon name="bell" size="sm" title="سررسید گذشته" />
                    {displayJalaliDate(p.dueDate)}
                    {p.daysOverdue
                      ? ` (${p.daysOverdue.toLocaleString('fa-IR')} روز گذشته)`
                      : ' (گذشته)'}
                  </span>
                ) : (
                  <span style={{ color: 'var(--sk-fg)', fontVariantNumeric: 'tabular-nums' }}>
                    {displayJalaliDate(p.dueDate)}
                  </span>
                )
              ) : (
                <span style={{ color: 'var(--sk-fg-muted)' }}>تعیین نشده</span>
              )}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: 'var(--sk-fg-muted)', marginBottom: 4 }}>وضعیت</div>
            <div style={{ fontWeight: 500 }}>{p.statusLabel}</div>
          </div>
        </div>
      </V2Card>

      {/* Team Modal / Panel */}
      {showTeamPanel ? (
        <ProjectTeamPanel
          slug={slug}
          projectRef={ref}
          projectName={p.name}
          onClose={() => {
            setShowTeamPanel(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      ) : null}

      {/* Status Transitions Card */}
      <V2Card>
        <V2CardHeader
          title="تغییر وضعیت پروژه"
          subtitle="گذار وضعیت بر اساس ماشین وضعیت مجاز پروژه"
        />
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 20px 20px' }}
        >
          {actions.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              این پروژه بسته شده است و وضعیت آن دیگر تغییر نمی‌کند.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {actions.map((act) => (
                <V2Button
                  key={act.to}
                  variant={act.variant}
                  disabled={pendingStatus !== null}
                  onClick={() =>
                    act.confirm ? setConfirmingAction(act) : void onTransitionStatus(act)
                  }
                >
                  {pendingStatus === act.to ? 'در حال ثبت…' : act.label}
                </V2Button>
              ))}
            </div>
          )}

          {confirmingAction ? (
            <Alert tone="warning" title="تأیید تغییر وضعیت">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span>{confirmingAction.confirm}</span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <V2Button
                    variant={confirmingAction.variant === 'danger' ? 'danger' : 'primary'}
                    size="sm"
                    onClick={() => void onTransitionStatus(confirmingAction)}
                  >
                    بله، {confirmingAction.label}
                  </V2Button>
                  <V2Button variant="ghost" size="sm" onClick={() => setConfirmingAction(null)}>
                    انصراف
                  </V2Button>
                </div>
              </div>
            </Alert>
          ) : null}

          {statusNotice ? (
            <Alert tone="success" title="ثبت شد">
              {statusNotice}
            </Alert>
          ) : null}
          {statusError ? (
            <Alert tone="danger" title="خطا">
              {statusError}
            </Alert>
          ) : null}
        </div>
      </V2Card>

      {/* Team Summary Card */}
      <V2Card>
        <V2CardHeader
          title="تیم پروژه"
          subtitle="اعضایی که امکان انتساب کار در این پروژه را دارند"
          actions={
            canManageTeam && !showTeamPanel ? (
              <V2Button
                variant="secondary"
                size="sm"
                icon="users"
                onClick={() => setShowTeamPanel(true)}
              >
                مدیریت تیم
              </V2Button>
            ) : null
          }
        />
        <div style={{ padding: '4px 20px 20px' }}>
          {team.status === 'loading' ? (
            <div style={{ display: 'flex', gap: 8, padding: 12 }}>
              <div
                className="sk-skel"
                style={{ height: 32, width: 120, borderRadius: 'var(--sk-r-pill)' }}
              />
              <div
                className="sk-skel"
                style={{ height: 32, width: 140, borderRadius: 'var(--sk-r-pill)' }}
              />
            </div>
          ) : teamMembers.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              هنوز عضوی در تیم این پروژه ثبت نشده است.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {teamMembers.map((m) => (
                <div
                  key={m.memberRef}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 12px',
                    borderRadius: 'var(--sk-r-pill)',
                    background: 'var(--sk-bg-canvas)',
                    border: '1px solid var(--sk-border-subtle)',
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: 'var(--sk-fg-muted)', display: 'inline-flex' }}>
                    <Icon name="users" size="sm" />
                  </span>
                  <span style={{ fontWeight: 500 }}>{m.displayLabel}</span>
                  {m.isOwner ? <V2Badge tone="brand">مدیر پروژه</V2Badge> : null}
                  {m.isSelf ? <V2Badge tone="neutral">شما</V2Badge> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </V2Card>

      {/* Task Summary KPI Strip */}
      <V2MetricStrip>
        <V2Kpi label="کل کارها" value={totalTasks.toLocaleString('fa-IR')} />
        <V2Kpi label="در حال انجام" value={inProgressTasks.toLocaleString('fa-IR')} />
        <V2Kpi label="انجام نشده" value={todoTasks.toLocaleString('fa-IR')} />
        <V2Kpi label="انجام‌شده" value={doneTasks.toLocaleString('fa-IR')} />
      </V2MetricStrip>

      {/* Project Tasks */}
      <V2Card clipped>
        <V2CardHeader
          title="کارهای پروژه"
          subtitle={`${p.tasks.length.toLocaleString('fa-IR')} کار در این پروژه ثبت شده است.`}
          actions={
            <V2Button
              variant="primary"
              size="sm"
              icon="plus"
              onClick={() => setShowNewTaskForm((v) => !v)}
            >
              کار جدید
            </V2Button>
          }
        />

        {showNewTaskForm ? (
          <form
            onSubmit={onCreateTask}
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--sk-divider)',
              background: 'var(--sk-bg-canvas)',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 14 }}>افزودن کار جدید به پروژه</div>
            <V2FormGrid>
              <V2FormField label="عنوان کار" required width="wide">
                <input
                  className="sk-input"
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  placeholder="عنوان کار…"
                  required
                />
              </V2FormField>

              <V2FormField label="مسئول">
                <Select
                  value={taskAssigneeRef}
                  onChange={(e) => setTaskAssigneeRef(e.target.value)}
                  aria-label="مسئول کار (فقط اعضای فعال پروژه)"
                >
                  <option value="">بدون مسئول</option>
                  {activeAssignableMembers.map((m) => (
                    <option key={m.memberRef} value={m.memberRef}>
                      {m.displayLabel} {m.isOwner ? '(مدیر پروژه)' : ''}
                    </option>
                  ))}
                </Select>
              </V2FormField>

              <V2FormField label="سررسید">
                <JalaliDateInput value={taskDueDate} onChange={setTaskDueDate} />
              </V2FormField>

              <V2FormField label="اولویت">
                <Select
                  value={taskPriority}
                  onChange={(e) => setTaskPriority(e.target.value)}
                  aria-label="اولویت کار"
                >
                  {TASK_PRIORITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </V2FormField>
            </V2FormGrid>

            <V2FormActions>
              <V2Button type="submit" variant="primary" size="sm" disabled={taskSubmitting}>
                {taskSubmitting ? 'در حال ثبت…' : 'ثبت کار'}
              </V2Button>
              <V2Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowNewTaskForm(false)}
              >
                انصراف
              </V2Button>
            </V2FormActions>

            {taskError ? (
              <Alert tone="danger" title="خطا">
                {taskError}
              </Alert>
            ) : null}
          </form>
        ) : null}

        {/* Tasks Table (Desktop) */}
        <div className="prj-detail__table">
          <V2Table
            columns={taskColumns}
            rows={taskRows}
            zebra
            emptyState={
              <V2EmptyState
                icon="tasks"
                compact
                title="هنوز کاری برای این پروژه ثبت نشده است."
                description="می‌توانید با دکمهٔ «کار جدید» اولین وظیفه را ثبت کنید."
              />
            }
          />
        </div>

        {/* Tasks Cards (Mobile) */}
        <div className="prj-detail__cards">
          {taskRows.length === 0 ? (
            <V2EmptyState
              icon="tasks"
              compact
              title="هنوز کاری برای این پروژه ثبت نشده است."
              description="می‌توانید با دکمهٔ «کار جدید» اولین وظیفه را ثبت کنید."
            />
          ) : (
            taskRows.map((t) => (
              <div key={t.id} className="sk-card prj-detail__card">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <Link
                    to={`${base}/projects/tasks/${encodeURIComponent(t.ref)}`}
                    style={{
                      flex: 1,
                      fontSize: 15,
                      fontWeight: 600,
                      color: 'var(--prj-link, var(--sk-brand-600))',
                      lineHeight: 1.5,
                      textDecoration: 'none',
                    }}
                  >
                    {t.title}
                  </Link>
                  <V2Badge tone={TASK_STATUS_TONE[t.statusLabel] ?? 'neutral'}>
                    {t.statusLabel}
                  </V2Badge>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: 'var(--sk-fg-muted)' }}>کد:</span>
                    <span className="ltr" style={{ fontFamily: 'var(--sk-font-mono)' }}>
                      {t.ref}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: 'var(--sk-fg-muted)' }}>مسئول:</span>
                    <span>{t.assignee?.displayLabel ?? 'بدون مسئول'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: 'var(--sk-fg-muted)' }}>سررسید:</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {displayJalaliDate(t.dueDate)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: 'var(--sk-fg-muted)' }}>اولویت:</span>
                    <span>{t.priorityLabel}</span>
                  </div>
                </div>

                <div>
                  <Link
                    to={`${base}/projects/tasks/${encodeURIComponent(t.ref)}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 44,
                      padding: '8px 16px',
                      borderRadius: 'var(--sk-r-md)',
                      background: 'var(--sk-bg-canvas)',
                      border: '1px solid var(--sk-border)',
                      color: 'var(--sk-fg)',
                      textDecoration: 'none',
                      fontWeight: 500,
                    }}
                  >
                    مشاهده کار
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      </V2Card>

      {/* Connected Procurements Card (B58 / X-05) */}
      <V2Card clipped>
        <V2CardHeader
          title="تدارکات و خریدهای پروژه"
          subtitle="درخواست‌ها و سفارش‌های خرید متصل به این پروژه"
          actions={
            <Link
              to={`${base}/procurement/requests`}
              className="sk-btn sk-btn--secondary sk-btn--sm"
            >
              + ثبت درخواست خرید
            </Link>
          }
        />
        <div style={{ padding: '0 20px 20px' }}>
          {procurements.status === 'loading' ? (
            <div style={{ padding: 12 }}>در حال بارگذاری تدارکات…</div>
          ) : procurements.status === 'ready' && procurements.data.items.length > 0 ? (
            <div className="sk-table-scroll">
              <table className="sk-table sk-table--compact">
                <thead>
                  <tr>
                    <th>درخواست خرید (PR)</th>
                    <th>وضعیت PR</th>
                    <th>مبلغ برآوردی PR</th>
                    <th>سفارش خرید (PO)</th>
                    <th>وضعیت PO</th>
                    <th>مبلغ سفارش (PO)</th>
                    <th>تاریخ سفارش</th>
                  </tr>
                </thead>
                <tbody>
                  {procurements.data.items.map((item, idx) => (
                    <tr key={`${item.requestCode ?? ''}-${item.orderCode ?? ''}-${idx}`}>
                      <td>
                        {item.requestCode ? (
                          <Link
                            to={`${base}/procurement/requests/${item.requestCode}`}
                            className="ltr font-mono"
                            style={{ fontWeight: 600, color: 'var(--prj-link, var(--sk-brand-600))' }}
                          >
                            {item.requestCode}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {item.requestStatus ? (
                          <V2Badge tone={item.requestStatus === 'approved' ? 'success' : 'neutral'}>
                            {item.requestStatus}
                          </V2Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="sk-num">
                        {item.requestTotalRial ? `${Number(item.requestTotalRial).toLocaleString('fa-IR')} ریال` : '—'}
                      </td>
                      <td>
                        {item.orderCode ? (
                          <Link
                            to={`${base}/procurement/orders/${item.orderCode}`}
                            className="ltr font-mono"
                            style={{ fontWeight: 600, color: 'var(--prj-link, var(--sk-brand-600))' }}
                          >
                            {item.orderCode}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {item.orderStatus ? (
                          <V2Badge tone={item.orderStatus === 'approved' ? 'success' : 'neutral'}>
                            {item.orderStatus}
                          </V2Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="sk-num">
                        {item.orderTotalRial ? `${Number(item.orderTotalRial).toLocaleString('fa-IR')} ریال` : '—'}
                      </td>
                      <td className="sk-num">
                        {item.orderDate ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <V2EmptyState
              compact
              icon="info"
              title="تدارکات یا خریدی برای این پروژه ثبت نشده است."
              description="می‌توانید با مشخص کردن این پروژه در فرم درخواست یا سفارش خرید، آن را به پروژه متصل کنید."
            />
          )}
        </div>
      </V2Card>

      {/* Comments Card */}
      <V2Card>
        <V2CardHeader
          title="نظرات و یادداشت‌ها"
          subtitle="تبادل نظر و یادداشت‌های مربوط به این پروژه"
        />
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 20px 20px' }}
        >
          <form
            onSubmit={onPostComment}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <V2FormGrid>
              <V2FormField label="متن نظر یا یادداشت" required width="full">
                <textarea
                  className="sk-textarea"
                  placeholder="متن نظر خود را وارد کنید…"
                  rows={3}
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                  required
                />
              </V2FormField>
            </V2FormGrid>
            <V2FormActions>
              <V2Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={commentSubmitting || !commentBody.trim()}
              >
                {commentSubmitting ? 'در حال ثبت…' : 'ثبت نظر'}
              </V2Button>
            </V2FormActions>
            {commentError ? (
              <Alert tone="danger" title="خطا">
                {commentError}
              </Alert>
            ) : null}
          </form>

          {p.comments.length === 0 ? (
            <V2EmptyState compact icon="info" title="هنوز نظری ثبت نشده است." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
              {p.comments.map((c) => (
                <div key={c.ref} className="prj-detail__comment">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      className="ltr"
                      style={{
                        fontSize: 12,
                        color: 'var(--sk-fg-muted)',
                        fontFamily: 'var(--sk-font-mono)',
                      }}
                    >
                      {c.ref}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                      {displayJalaliDateTime(c.createdAt)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: 'var(--sk-fg)',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                    }}
                  >
                    {c.body}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </V2Card>
    </div>
  );
}

function labelOf(status: ProjectStatus): string {
  return PROJECT_STATUS_OPTIONS.find((opt) => opt.value === status)?.label ?? status;
}
